import {
  FormField,
  FormFieldType,
  FormPrimitiveValues,
  FormPrimitiveValuesArray,
  FormPrimitiveValuesObject,
  IFormRadioGroup,
  IFormSelect,
} from '@/forms/typings';
import { getStoreState } from '@/store';
import { adaptFormDataToFormField } from './formDataToFormField.adapter';
import { UpdateSectionRequestDTO } from '@/api/section/DTOs';

import {
  expandFieldValuesToPrimitives,
  mapPrimitiveToFormFieldValueDto,
  resolveSectionFormFieldItemId,
  type MapPrimitiveToFormFieldValueContext,
  type SectionFieldLookupSnapshot,
} from './formFieldValuesToSectionFormFieldUpdateRequest.mapping';

export type {
  MapPrimitiveToFormFieldValueContext,
  SectionFieldLookupSnapshot,
} from './formFieldValuesToSectionFormFieldUpdateRequest.mapping';

export {
  expandFieldValuesToPrimitives,
  mapPrimitiveToFormFieldValueDto,
  resolveSectionFormFieldItemId,
} from './formFieldValuesToSectionFormFieldUpdateRequest.mapping';

const getLookupSnapshot = (): SectionFieldLookupSnapshot => {
  const { internalLookups, externalLookups } = getStoreState().lookup;
  return { internalLookups, externalLookups };
};

const isLookupField = (
  field: FormField,
): field is IFormSelect | IFormRadioGroup => {
  return [FormFieldType.Select, FormFieldType.RadioGroup].includes(field.type);
};

export const adaptFormFieldValuesToSectionFormFieldUpdateRequest = (
  fieldValues:
    | FormPrimitiveValues
    | FormPrimitiveValuesArray
    | FormPrimitiveValuesObject[]
    | undefined,
  fieldId: string,
  formData: FormField[],
): UpdateSectionRequestDTO['formField'] => {
  if (fieldValues === undefined) {
    return {
      itemID: resolveSectionFormFieldItemId(fieldId, false),
    };
  }

  let formFieldType: FormField['type'] = FormFieldType.Input;
  let useShortName = false;

  const flatData = adaptFormDataToFormField(formData);

  const field = flatData.find(f => {
    return f.fieldId === fieldId;
  });

  if (field) {
    formFieldType = field.type as FormField['type'];
    const isNarrowed = isLookupField(field as FormField);
    useShortName =
      (isNarrowed && (field as IFormSelect | IFormRadioGroup).useShortName) ??
      false;
  }

  const primitives = expandFieldValuesToPrimitives(fieldValues);
  const lookups = getLookupSnapshot();

  const isMasked =
    Boolean(field) &&
    field!.type === FormFieldType.Input &&
    field!.mask !== undefined;

  const mapCtxBase: Omit<MapPrimitiveToFormFieldValueContext, 'isMasked'> = {
    formFieldType,
    useShortName,
    fieldId,
    lookups,
  };

  const values = primitives.map(p =>
    mapPrimitiveToFormFieldValueDto(p, {
      ...mapCtxBase,
      isMasked: primitives.length === 1 ? isMasked : false,
    }),
  );

  return {
    itemID: resolveSectionFormFieldItemId(fieldId, true),
    values,
  };
};
